/**
 * The dark bubble a chip answers with.
 *
 * Every status chip on a keeper row is a fact with a story behind it — which farm auction, who
 * dropped him, why he cannot be franchised — and the row has no space for any of it. So the chip
 * is a button and the story is one tap away.
 *
 * Three things it has to get right:
 *
 *   IT MUST NOT COVER THE ROW IT IS ABOUT. Anchored to the top of the chip, never below it. A
 *   tooltip that hides the thing it annotates makes the reader tap it twice to check.
 *   IT MUST NOT FALL OFF THE SCREEN. Anchored left by default and flipped to right when that would
 *   overflow, measured after it opens rather than guessed from the chip's position.
 *   IT MUST OPEN THE WAY THE DEVICE OPENS THINGS. Tap on touch, hover on a mouse. The same
 *   component does both because `pointer: coarse` is the only thing that decides.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { dock } from '../dock.js';
import { isCoarsePointer } from '../pointer.js';

export function Callout(
  { label, text, by, tone = '', className = '', ariaLabel }:
  {
    label: ReactNode; text: string; by?: string; tone?: string; className?: string;
    /**
     * A fuller accessible name for the button, when the chip's own text is an abbreviation of
     * the fact. "In college" reads as the whole story to a sighted reader who can see the school
     * in the row beside it; to a screen reader it is three words with no subject. Optional — a
     * chip whose label already IS the sentence passes nothing and keeps its text as its name.
     */
    ariaLabel?: string;
  },
) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<
    { left: number; top: number; arrow: number; below: boolean } | null
  >(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const id = useId();
  const touch = isCoarsePointer();

  /*
   * IT IS RENDERED INTO THE BODY, not into the row.
   *
   * The bubble used to be absolutely positioned inside its chip, which works right up until an
   * ancestor clips. Three of them do: the list card hides its overflow so the rows' hairlines
   * stop at its rounded corners, the escalation strip animates by growing `max-height` out of
   * `overflow: hidden`, and the desktop rail is its own scrolling column. Any one of those cuts
   * the bubble in half, and the fix is not to unclip them — the card needs its corners and the
   * strip needs its animation. A portal leaves the clipping where it belongs and takes the one
   * element that must escape it out of the tree.
   *
   * Fixed positioning, measured from the chip on open. It is not tracked during a scroll: the
   * bubble closes on any pointer-down outside it, so a scroll that would move it has already
   * dismissed it.
   */
  useLayoutEffect(() => {
    if (!open || !wrap.current || !bubble.current) { setBox(null); return; }
    const anchor = wrap.current.getBoundingClientRect();
    const b = bubble.current.getBoundingClientRect();
    const GAP = 10;
    // Centred on the chip, then pushed back inside whichever edge it would have crossed.
    let left = anchor.left + anchor.width / 2 - b.width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - b.width - 8));
    // Above the chip, because a bubble must never cover the row it is about. Below only when
    // there is no room above at all.
    const above = anchor.top - b.height - GAP;
    const below = above < 8;
    const top = below ? anchor.bottom + GAP : above;
    /*
      The caret points back at the chip. Measured from the bubble's own left edge, because the
      bubble has already been pushed inside the viewport and the two no longer share a centre —
      a caret drawn at 50% would point at the middle of the bubble instead of at the thing that
      opened it. Kept off the rounded corners at both ends.
    */
    const centre = anchor.left + anchor.width / 2 - left;
    const arrow = Math.max(12, Math.min(centre, b.width - 12));
    setBox({ left, top, arrow, below });
  }, [open, text]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e: Event) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // Capture, because a chip inside a row whose own handler stops propagation would otherwise
    // leave the bubble up for the rest of the session.
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  return (
    <span className={`kcowrap ${className}`} ref={wrap}
      onMouseEnter={touch ? undefined : () => setOpen(true)}
      onMouseLeave={touch ? undefined : () => setOpen(false)}>
      {/*
        On a mouse the hover has already opened it, so a click would toggle it straight back
        shut — which is what happened the first time this was driven on a desktop. The click is
        the touch device's way in and only the touch device's.
      */}
      <button type="button" className="kcobtn" aria-describedby={open ? id : undefined}
        aria-expanded={open} aria-label={ariaLabel}
        onClick={touch ? (e) => { e.stopPropagation(); setOpen((v) => !v); } : undefined}>
        <span className={`kchip ${tone}`}>{label}</span>
      </button>
      {open && dock(
        <span
          className={`kcallout${box?.below ? ' below' : ''}`}
          role="tooltip"
          id={id}
          ref={bubble}
          /* Hidden until measured, so it never flashes at 0,0 on the way to where it belongs. */
          style={box
            ? {
              left: `${box.left}px`,
              top: `${box.top}px`,
              ['--kc-arrow' as string]: `${box.arrow}px`,
            }
            : { left: 0, top: 0, visibility: 'hidden' }}
        >
          {text}
          {/* Who did it, under a rule and set in bold — a different kind of fact from the two
              above it, so it is drawn as one rather than as a third line. */}
          {by && <b className="kcoby">by {by}</b>}
          <i aria-hidden="true" />
        </span>,
      )}
    </span>
  );
}
